import { Box } from '@mui/material';
import { Mail } from 'lucide-react';
import { useEffect, useRef, useState, type ReactElement, type SyntheticEvent } from 'react';
import { HttpError, useLogin } from 'react-admin';

import { extractErrorCode } from '@/api/apiError';
import { Field } from '@/ui/kit';

import { AuthLayout } from './AuthLayout';
import { AuthLink } from './AuthLink';
import { AuthNotice } from './AuthNotice';
import { AuthSubmitButton } from './AuthSubmitButton';
import { clearLoginNotice, peekLoginNotice, type LoginNoticeKind } from './loginNotice';
import { PasswordField } from './PasswordField';
import { FORGOT_PASSWORD_PATH } from './routes';

/**
 * Ecran de connexion (variante « Q » de la maquette P01).
 *
 * Ecrit plutot que repris de `<Login>` de React Admin : le champ est une
 * **adresse e-mail**, l'erreur doit rester **sous les yeux** pendant qu'on
 * corrige le mot de passe (une notification s'efface), et le refus d'un compte
 * sans role d'administration a besoin d'une explication, pas d'un 403 plus tard.
 *
 * Le comportement vit dans l'`authProvider` (`login` lit `GET /admin/me` et ferme
 * la session d'un compte sans role) ; cet ecran ne fait que le montrer. Les
 * messages sont ceux que le client HTTP a deja traduits (`api/apiError.ts`) :
 * identifiants faux, trop de tentatives, API injoignable, maintenance.
 *
 * Aucun mot de passe n'est journalise, ni en cas de succes ni en cas d'echec.
 */

const NOTICES: Readonly<Record<LoginNoticeKind, { readonly tone: 'error' | 'success'; readonly text: string }>> = {
  session_expired: { tone: 'error', text: 'Session expirée. Reconnectez-vous.' },
  password_replaced: { tone: 'success', text: 'Mot de passe remplacé. Reconnectez-vous.' },
};

const FALLBACK_MESSAGE = 'La connexion a échoué. Réessayez.';

export const LoginPage = (): ReactElement => {
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Lu une fois, au montage : le message d'une session expiree vaut pour cette
  // ouverture de l'ecran, pas pour les suivantes.
  const [notice, setNotice] = useState<LoginNoticeKind | null>(() => peekLoginNotice());
  const passwordInput = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // Garde synchrone : l'etat `submitting` ne se met a jour qu'au rendu suivant,
  // et deux envois peuvent partir dans l'intervalle (Entree repetee).
  const inFlight = useRef(false);

  useEffect(() => {
    clearLoginNotice();
  }, []);

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (inFlight.current || email.trim() === '' || password === '') {
      return;
    }

    inFlight.current = true;
    setError(null);
    setNotice(null);
    setSubmitting(true);

    login({ username: email, password })
      .catch((cause: unknown) => {
        setError(cause instanceof Error && cause.message !== '' ? cause.message : FALLBACK_MESSAGE);

        // Identifiants faux : l'adresse reste, et le mot de passe est selectionne
        // pour etre retape d'un coup, sans effacer a la main.
        if (cause instanceof HttpError && extractErrorCode(cause.body) === 'invalid_credentials') {
          passwordInput.current?.focus();
          passwordInput.current?.select();
        }
      })
      .finally(() => {
        inFlight.current = false;
        setSubmitting(false);
      });
  };

  const shownNotice = notice === null ? null : NOTICES[notice];

  return (
    <AuthLayout
      title="Ravi de vous revoir"
      documentTitle="Connexion"
      subtitle="Entrez vos identifiants pour continuer."
    >
      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}
      {shownNotice === null || error !== null ? null : (
        <AuthNotice tone={shownNotice.tone}>{shownNotice.text}</AuthNotice>
      )}

      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <Field
          label="E-mail"
          type="email"
          size="L"
          icon={Mail}
          value={email}
          onChange={setEmail}
          placeholder="vous@exemple.fr"
          autoComplete="username"
          autoFocus
          required
        />

        <PasswordField
          label="Mot de passe"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          inputRef={passwordInput}
          required
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AuthLink to={FORGOT_PASSWORD_PATH}>Mot de passe oublié ?</AuthLink>
        </Box>

        <AuthSubmitButton busy={submitting} disabled={email.trim() === '' || password === ''}>
          {submitting ? 'Connexion…' : 'Se connecter'}
        </AuthSubmitButton>
      </Box>
    </AuthLayout>
  );
};
